import React, { forwardRef } from 'react';
import { Controller } from 'react-hook-form';
import TextField from '@mui/material/TextField';
import FormHelperText from '@mui/material/FormHelperText';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import NRMessageList from './NRMessageList';

function NRControlledTextField(
  {
    className,
    size,
    id,
    name,
    label,
    variant,
    multiline = false,
    rows,
    helperText,
    helperErrorText,
    control,
    rules,
    error,
    disabled,
    defaultValue,
    placeholder,
    color = 'secondary',
    onEnter,
    setError,
    onChange: parentOnChange
  },
  ref
) {
  // Render error helper text (supports list or single message)
  const renderHelperText = msg => {
    if (!msg) return null;

    // Case 1: object with title + rules
    if (typeof msg === 'object' && msg.title && Array.isArray(msg.rules)) {
      return (
        <Box>
          <Typography color="error" variant="caption" fontWeight="bold" display="block" mb={0.5}>
            {msg.title}
          </Typography>
          <NRMessageList rules={msg.rules} />
        </Box>
      );
    }

    // Case 2: array of strings
    if (Array.isArray(msg)) {
      return <NRMessageList rules={msg} />;
    }

    // Case 3: single string
    return (
      <Box>
        <Typography color="error" variant="caption">
          {msg}
        </Typography>
      </Box>
    );
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      defaultValue={defaultValue}
      render={({ field }) => (
        <>
          <TextField
            {...field}
            inputRef={ref}
            className={className}
            size={size}
            id={id}
            label={label}
            variant={variant}
            multiline={multiline}
            rows={rows}
            fullWidth
            disabled={disabled}
            error={!!error}
            color={color}
            placeholder={placeholder}
            onChange={e => {
              field.onChange(e);
              if (setError && error && e.target.value === '') {
                setError('');
              }
              if (typeof parentOnChange === 'function') {
                parentOnChange(e.target.value);
              }
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' && onEnter) {
                e.preventDefault();
                onEnter(field.value);
              }
              if (e.key === 'Escape') {
                e.stopPropagation();
                e.target.blur();
              }
            }}
            helperText={helperText}
          />
          {!!error && (
            <FormHelperText error sx={{ pl: 2 }}>
              {renderHelperText(helperErrorText)}
            </FormHelperText>
          )}
        </>
      )}
    />
  );
}

export default forwardRef(NRControlledTextField);
